import { useMemo, useState, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { ErrorMessage } from '@calendly/form'
import { httpRequest } from '@calendly/http-tools'
import styled from 'astroturf/react'
const colorBackgroundSecondary = 'var(--colorBackgroundSecondary, var(--coreColorNeutral100))'

import { Settings } from 'services/settings'
import RoutesHelper from 'services/asset-path'
import { useI18n } from 'locales'

import { useInvoicePageData } from './InvoiceContext'
import _LoadingPlaceholder from "InvoiceStripeContainer-LoadingPlaceholder.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/invoice/InvoiceStripeContainer.js?styleId=LoadingPlaceholder";


const LoadingPlaceholder = /*#__PURE__*/styled("div", null, {
  displayName: "LoadingPlaceholder",
  styles: _LoadingPlaceholder
})

const InvoiceStripeContainer = ({ children }) => {
  const { i18n } = useI18n()
  const { invoice } = useInvoicePageData()
  const invoiceUuid = invoice?.uuid
  const [fetchError, setFetchError] = useState(null)
  const [stripeLoadError, setStripeLoadError] = useState(null)
  const [clientSecretData, setClientSecretData] = useState({
    status: 'idle',
    value: null,
    stripeAccount: null,
  })

  const stripePromise = useMemo(
    () =>
      loadStripe(
        Settings.stripe.publishable_key,
        clientSecretData.stripeAccount
          ? { stripeAccount: clientSecretData.stripeAccount }
          : {},
      ),
    [clientSecretData.stripeAccount],
  )

  useEffect(() => {
    if (!invoiceUuid || clientSecretData.status !== 'idle') return

    setClientSecretData((prev) => ({ ...prev, status: 'loading' }))

    const fetchSecret = async () => {
      const [ok, res] = await httpRequest({
        url: `/api/booking/invoices/${invoiceUuid}/stripe_payment_intents`,
        method: 'POST',
      })

      if (ok && res?.resource) {
        setClientSecretData({
          status: 'loaded',
          value: res.resource.client_secret,
          stripeAccount: res.resource.stripe_account,
        })
      } else {
        setFetchError(i18n.t('errors.billing.card.payment_form_not_loaded'))
      }
    }

    fetchSecret()
  }, [invoiceUuid, clientSecretData.status, i18n])

  useEffect(() => {
    async function awaitStripePromise() {
      try {
        await stripePromise
      } catch (_e) {
        setStripeLoadError(i18n.t('errors.billing.card.payment_form_not_loaded'))
      }
    }
    awaitStripePromise()
  }, [i18n, stripePromise])

  const error = fetchError || stripeLoadError
  if (error) {
    return <ErrorMessage message={error} />
  }

  if (clientSecretData.status !== 'loaded') {
    return <LoadingPlaceholder />
  }

  const options = {
    locale: i18n.locale,
    fonts: [{ cssSrc: RoutesHelper.assetUrl('external/proxima-nova.css') }],
    clientSecret: clientSecretData.value,
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      {typeof children === 'function'
        ? children(clientSecretData.value)
        : children}
    </Elements>
  )
}

export default InvoiceStripeContainer
